import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { supabase } from '@/integrations/supabase/client';
import { useTenant } from '@/hooks/useTenant';
import { fetchStaff, type StaffMember } from '@/lib/api/staff';

const COMPLETED_STATUSES = new Set(['READY', 'SERVED']);
const LOOKBACK_DAYS_FOR_UPTIME = 7;

type RestaurantStatusRecord = {
  id: string;
  name: string;
  is_active: boolean | null;
  is_demo: boolean;
  demo_expires_at: string | null;
  updated_at: string;
};

export type SidebarInfoSnapshot = {
  name: string;
  status: 'online' | 'offline';
  statusLabel: string;
  activeStaff: number;
  totalStaff: number;
  uptimePercent: number;
  isDemo: boolean;
  lastUpdatedAt: string | null;
  registerCount?: number;
  activeAssociates?: number;
  totalAssociates?: number;
};

export interface SidebarInfoResult {
  info: SidebarInfoSnapshot;
  isLoading: boolean;
  isError: boolean;
}

const clampPercentage = (value: number): number => {
  if (!Number.isFinite(value)) {
    return 0;
  }
  if (value < 0) {
    return 0;
  }
  if (value > 100) {
    return 100;
  }
  return value;
};

const calculateUptime = (orders: { status: string | null }[]): number => {
  if (orders.length === 0) {
    return 100;
  }
  const completed = orders.filter((order) => order.status && COMPLETED_STATUSES.has(order.status)).length;
  const raw = (completed / orders.length) * 100;
  return clampPercentage(Number(raw.toFixed(1)));
};

const toIsoDaysAgo = (days: number): string => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() - days);
  return date.toISOString();
};

export const useRestaurantSidebarInfo = () => {
  const { restaurantData } = useTenant();

  const restaurantId = restaurantData?.restaurant_id ?? null;
  const restaurantName = restaurantData?.name ?? restaurantData?.subdomain ?? 'Restaurant';

  const restaurantQuery = useQuery<RestaurantStatusRecord | null>({
    queryKey: ['layout', 'restaurant', restaurantId],
    enabled: Boolean(restaurantId),
    queryFn: async () => {
      if (!restaurantId) {
        return null;
      }
      const { data, error } = await supabase
        .from('restaurants')
        .select('id, name, is_active, is_demo, demo_expires_at, updated_at')
        .eq('id', restaurantId)
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('[sidebar] failed to fetch restaurant status', error);
        throw error;
      }

      return (data ?? null) as RestaurantStatusRecord | null;
    },
  });

  const staffQuery = useQuery<{ staff: StaffMember[] }>({
    queryKey: ['layout', 'staff', restaurantId],
    enabled: Boolean(restaurantId),
    queryFn: ({ signal }) => fetchStaff({ signal }),
  });

  const uptimeQuery = useQuery<number>({
    queryKey: ['layout', 'uptime', restaurantId, LOOKBACK_DAYS_FOR_UPTIME],
    enabled: Boolean(restaurantId),
    queryFn: async () => {
      if (!restaurantId) {
        return 100;
      }

      const fromIso = toIsoDaysAgo(LOOKBACK_DAYS_FOR_UPTIME);
      const { data, error } = await supabase
        .from('orders')
        .select('status')
        .eq('restaurant_id', restaurantId)
        .gte('created_at', fromIso)
        .order('created_at', { ascending: false })
        .limit(250);

      if (error) {
        console.error('[sidebar] failed to fetch recent orders for uptime', error);
        throw error;
      }

      const upt = calculateUptime((data ?? []) as { status: string | null }[]);
      return upt;
    },
  });

  const info = useMemo<SidebarInfoSnapshot>(() => {
    const restaurant = restaurantQuery.data;
    const staff = staffQuery.data?.staff ?? [];
    const activeStaff = staff.filter((member) => member.status !== 'inactive').length;
    const totalStaff = staff.length;

    const status = restaurant?.is_active === false ? 'offline' : 'online';
    const statusLabel = status === 'online' ? 'Online' : 'Offline';
    const uptimePercent = uptimeQuery.data ?? 100;

    return {
      name: restaurantName,
      status,
      statusLabel,
      activeStaff,
      totalStaff,
      uptimePercent,
      isDemo: restaurant?.is_demo ?? false,
      lastUpdatedAt: restaurant?.updated_at ?? null,
    };
  }, [restaurantQuery.data, staffQuery.data, uptimeQuery.data, restaurantName]);

  const isLoading = restaurantQuery.isLoading || staffQuery.isLoading || uptimeQuery.isLoading;
  const isError = restaurantQuery.isError || staffQuery.isError || uptimeQuery.isError;

  return {
    info,
    isLoading,
    isError,
  };
};
